import { useState } from "react";
import "./DashboardHeader.css";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useLanguage } from '../../../utils/languageContext';
import { useCommon } from "../../../context/CommonContext";
import { rchAPI } from "../../../services/rch.api";
import { eopdAPI } from "../../../services/e-opd.api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const T = {
  ink: "#111C33",
  muted: "#5A657D",
  blue: "#1E88E5",
  blueDark: "#1565C0",
  blueSoft: "#E8F3FE",
  border: "#E5EAF2",
};

const DashboardHeader = ({ onToggleSidebar, onToggleMenu, onLanguageClick }) => {
  const { currentLanguage, t } = useLanguage();

  const navigate = useNavigate();

  const { setNcdUser } = useCommon();

  const [anchorEl, setAnchorEl] = useState(null);
  const profileOpen = Boolean(anchorEl);
  const closeProfile = () => setAnchorEl(null);

  const isLoggedIn = Boolean(
    localStorage.getItem("SKT_token") ||
    localStorage.getItem("NCD_token") ||
    localStorage.getItem("sickle_token") ||
    localStorage.getItem("PMNDP_token") ||
    localStorage.getItem("uwin_token") ||
    localStorage.getItem("JANANI_token") ||
    localStorage.getItem("RBSK_token") ||
    localStorage.getItem("e-OPD_token") ||
    localStorage.getItem("NIKSHAY_valid_user")
  );

  const handleLogout = async () => {
    closeProfile();

    const jananiRefreshToken = localStorage.getItem("JANANI_refreshToken");

    if (jananiRefreshToken) {
      try {
        const response = await rchAPI.rchLogout({ refreshToken: jananiRefreshToken });
        if (response?.success) {
          toast.success(response?.message || "JANANI Logged out successfully");
          // Proceed to cleanup
        } else {
          toast.error(response?.message || "JANANI logout failed");
          return; // Stop logout if API failed
        }
      } catch (error) {
        toast.error("JANANI logout failed. Please try again.");
        return; // Stop logout if API threw error
      }
    }

    const eopdToken = localStorage.getItem("e-OPD_token");
    if (eopdToken) {
      try {
        const response = await eopdAPI.logout();
        if (response?.success) {
          toast.success(response?.message || "e-OPD Logged out successfully");
        } else {
          toast.error(response?.message || "e-OPD logout failed");
          return; // Stop logout if API failed
        }
      } catch (error) {
        console.error("e-OPD logout error:", error);
        toast.error("e-OPD logout failed. Please try again.");
        return; // Stop logout if API threw error
      }
    }

    // Remove tokens from local storage
    localStorage.removeItem("SKT_token");
    localStorage.removeItem("NCD_token");
    localStorage.removeItem("sickle_token");
    localStorage.removeItem("sickle_refresh_token");
    localStorage.removeItem("NIKSHAY_valid_user")
    localStorage.removeItem("uwin_token");
    localStorage.removeItem("JANANI_token");
    localStorage.removeItem("JANANI_refreshToken");
    localStorage.removeItem("JANANI_loggedinUserdetails");
    localStorage.removeItem("janani_username");
    localStorage.removeItem("RBSK_token");
    localStorage.removeItem("RBSK_loggedinUserdetails");
    localStorage.removeItem("rbsk_username");
    localStorage.removeItem("e-OPD_token");
    localStorage.removeItem("e-OPD_refresh_token");
    localStorage.removeItem("e-OPD_loggedinUserdetails");
    localStorage.removeItem("e-OPD_username");

    localStorage.clear();
    sessionStorage.clear();
    setNcdUser(null);

    navigate("/login");
  };

  const iconBtnSx = {
    width: 36,
    height: 36,
    borderRadius: "10px",
    color: T.muted,
    transition: "background-color .2s ease, color .2s ease",
    "&:hover": { backgroundColor: T.blueSoft, color: T.blue },
  };

  return (
    <header className="dashboard-header">
      <Tooltip title="Toggle menu" arrow>
        <IconButton
          className="menuCollapse"
          onClick={onToggleMenu}
          aria-label="Toggle menu"
          sx={{ ...iconBtnSx, mr: "auto" }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Box className="icons" key={currentLanguage} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        {/* Language — abhi disabled hai, par ab disabled jaisa dikhta bhi hai */}
        <Tooltip title="Language selection coming soon" arrow>
          <span>
            <Box
              component="button"
              type="button"
              onClick={onLanguageClick}
              disabled
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.1,
                py: 0.5,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                backgroundColor: "#fff",
                color: T.muted,
                cursor: "not-allowed",
                opacity: 0.65,
                font: "inherit",
              }}
            >
              <LanguageRoundedIcon sx={{ fontSize: 16 }} />
              <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1 }}>
                {currentLanguage}
              </Typography>
            </Box>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5, borderColor: T.border }} />

        <Tooltip title={t('menu.notifications')} arrow>
          <IconButton aria-label={t('menu.notifications')} sx={iconBtnSx}>
            <Badge color="error" variant="dot" overlap="circular">
              <NotificationsNoneRoundedIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title={t('menu.synchronize')} arrow>
          <IconButton aria-label={t('menu.synchronize')} sx={iconBtnSx}>
            <SyncRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('menu.userProfile')} arrow>
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-label={t('menu.userProfile')}
            aria-haspopup="true"
            aria-expanded={profileOpen ? "true" : undefined}
            sx={{ p: 0.4, ml: 0.25 }}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                backgroundColor: T.blueSoft,
                color: T.blueDark,
                border: `1px solid ${T.border}`,
              }}
            >
              <PersonOutlineRoundedIcon sx={{ fontSize: 18 }} />
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={profileOpen}
          onClose={closeProfile}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                mt: 1,
                minWidth: 190,
                borderRadius: "12px",
                border: `1px solid ${T.border}`,
                boxShadow: "0 8px 24px rgba(16,24,40,.10)",
                "& .MuiMenuItem-root": {
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: T.ink,
                  py: 1,
                  "&:hover": { backgroundColor: T.blueSoft },
                },
                "& .MuiListItemIcon-root": { minWidth: 30, color: T.muted },
              },
            },
          }}
        >
          <MenuItem onClick={() => { closeProfile(); navigate('/myprofile'); }}>
            <ListItemIcon>
              <PersonOutlineRoundedIcon fontSize="small" />
            </ListItemIcon>
            {t('menu.myProfile')}
          </MenuItem>

          {isLoggedIn && [
            <Divider key="div" sx={{ borderColor: T.border }} />,
            <MenuItem key="logout" onClick={handleLogout} sx={{ color: "#DC2626 !important" }}>
              <ListItemIcon>
                <LogoutRoundedIcon fontSize="small" sx={{ color: "#DC2626" }} />
              </ListItemIcon>
              {t('menu.logout')}
            </MenuItem>,
          ]}
        </Menu>
      </Box>
    </header>
  );
};
export default DashboardHeader;
